import "bootstrap-icons/font/bootstrap-icons.css";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token");
  if (t) return String(t).trim();
  try { const u=JSON.parse(localStorage.getItem("user")||"{}"); return String(u?.access_token||u?.token||"").trim(); } catch { return ""; }
}
function getAuthHeaders() {
  const h={Accept:"application/json"}; const t=getBearerToken();
  if (t) h.Authorization=`Bearer ${t}`; return h;
}
async function apiGet(path) {
  if (!EVENTS_BASE) throw new Error("Events API URL not configured.");
  const res  = await fetch(`${EVENTS_BASE}${path}`, { headers:getAuthHeaders(), cache:"no-store" });
  const json = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
  return json;
}

const fmt = (n) => `BTN. ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("en-US",{timeZone:"Asia/Thimphu",month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit",hour12:true}) : "—";

const REV_PAGE = 10;
const TXN_PAGE = 15;

const revTblStyle = { tableLayout:"fixed", width:"100%", minWidth:"650px" };
const txnTblStyle = { tableLayout:"fixed", width:"100%", minWidth:"750px" };
const nowrap = { whiteSpace:"nowrap" };

export default function OrganizerRevenueDetail() {
  useIdleLogout();

  const { id }        = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") || "revenue";

  /* organizer name — from router state or resolved from API response */
  const [orgName, setOrgName] = useState(location.state?.orgName || "");

  /* ---- Revenue state ---- */
  const [revData,    setRevData]    = useState(null);
  const [revLoading, setRevLoading] = useState(false);
  const [revErr,     setRevErr]     = useState("");
  const [revPage,    setRevPage]    = useState(1);

  /* ---- Transactions state ---- */
  const [txns,       setTxns]       = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnErr,     setTxnErr]     = useState("");
  const [txnPage,    setTxnPage]    = useState(1);

  const fetchRevenue = useCallback(async () => {
    try {
      setRevLoading(true); setRevErr("");
      const json = await apiGet(`/organizers/${id}/revenue`);
      const d = json?.data || json || {};
      setRevData(d);
      if (!orgName && d.organizer_name) setOrgName(d.organizer_name);
    } catch(e) { setRevErr(e.message||"Failed to load revenue."); }
    finally { setRevLoading(false); }
  }, [id, orgName]);

  const fetchTransactions = useCallback(async () => {
    try {
      setTxnLoading(true); setTxnErr("");
      const json = await apiGet(`/organizers/${id}/wallet/transactions`);
      setTxns(Array.isArray(json?.data) ? json.data : []);
    } catch(e) { setTxnErr(e.message||"Failed to load transactions."); }
    finally { setTxnLoading(false); }
  }, [id]);

  useEffect(() => {
    if (tab === "revenue")      fetchRevenue();
    if (tab === "transactions") fetchTransactions();
  }, [tab, fetchRevenue, fetchTransactions]);

  const switchTab = (t) => {
    setSearchParams({ tab: t }, { replace: true });
    setRevPage(1);
    setTxnPage(1);
  };

  /* ---- Revenue pagination ---- */
  const evts          = revData?.events || [];
  const revTotal      = evts.length;
  const revTotalPages = Math.max(1, Math.ceil(revTotal / REV_PAGE));
  const revSlice      = evts.slice((revPage-1)*REV_PAGE, revPage*REV_PAGE);

  /* ---- Transactions pagination ---- */
  const txnTotalPages = Math.max(1, Math.ceil(txns.length / TXN_PAGE));
  const txnSlice      = txns.slice((txnPage-1)*TXN_PAGE, txnPage*TXN_PAGE);

  const displayName = orgName || "Organizer";

  return (
    <>
      {/* BACK + HEADER */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-outline-secondary btn-sm px-3" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-1"/>Back
        </button>
        <div>
          <h5 className="mb-0 fw-bold">{displayName}</h5>
          <small className="text-muted" style={{fontSize:11}}>
            ID: {id.length > 20 ? `${id.slice(0,8)}…${id.slice(-6)}` : id}
          </small>
        </div>
      </div>

      {/* TAB SWITCHER */}
      <div className="card mb-4 search-filter-card">
        <div className="card-body searchbar-row-inline" style={{padding:"10px 14px"}}>
          <div className="d-flex gap-2">
            <button
              className={`btn ${tab==="revenue" ? "btn-primary" : "btn-outline-secondary"}`}
              style={{borderRadius:999,fontWeight:600,fontSize:14,padding:"6px 22px"}}
              onClick={() => switchTab("revenue")}
            >
              <i className="bi bi-bar-chart me-1"/>Revenue
            </button>
            <button
              className={`btn ${tab==="transactions" ? "btn-primary" : "btn-outline-secondary"}`}
              style={{borderRadius:999,fontWeight:600,fontSize:14,padding:"6px 22px"}}
              onClick={() => switchTab("transactions")}
            >
              <i className="bi bi-clock-history me-1"/>Transactions
            </button>
          </div>
        </div>
      </div>

      {/* ===== REVENUE TAB ===== */}
      {tab === "revenue" && (
        <>
          {revErr && <div className="alert alert-danger">{revErr}</div>}
          {revLoading && <div className="text-center py-5"><span className="spinner-border"/></div>}
          {!revLoading && revData && (
            <>
              {/* Summary metrics */}
              <div className="row row-cols-1 row-cols-md-3 g-3 mb-4 events-metrics">
                {[
                  { label:"Gross Revenue",  value: fmt(revData.gross_revenue)                  },
                  { label:"Tickets Sold",   value: (revData.tickets_sold||0).toLocaleString()  },
                  { label:"Total Bookings", value: (revData.total_bookings||0).toLocaleString() },
                ].map((m,i) => (
                  <div className="col" key={i}>
                    <div className="card metrics-card h-100">
                      <div className="card-body">
                        <h6 className="text-secondary mb-1">{m.label}</h6>
                        <div className="fs-2 fw-bold">{m.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Events breakdown table */}
              <div className="bookings-table-card card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Events Revenue Breakdown</h5>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle table-pro" style={revTblStyle}>
                    <colgroup>
                      <col style={{width:"5%"}}/>
                      <col style={{width:"33%"}}/>
                      <col style={{width:"14%"}}/>
                      <col style={{width:"20%"}}/>
                      <col style={{width:"14%"}}/>
                      <col style={{width:"14%"}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th>SI.NO</th>
                        <th>Event</th>
                        <th>Category</th>
                        <th>Revenue</th>
                        <th>Tickets</th>
                        <th>Bookings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evts.length===0 && (
                        <tr><td colSpan={6} className="text-center py-4 text-muted">No events found.</td></tr>
                      )}
                      {revSlice.map((ev,i) => (
                        <tr key={ev.id||i}>
                          <td style={nowrap}>{(revPage-1)*REV_PAGE+i+1}</td>
                          <td className="fw-semibold" style={{fontSize:13,whiteSpace:"normal",overflowWrap:"break-word"}}>
                            {ev.title||"—"}
                          </td>
                          <td style={nowrap}><span className="badge bg-secondary">{ev.category||"—"}</span></td>
                          <td className="mono" style={nowrap}>{fmt(ev.revenue)}</td>
                          <td className="mono" style={nowrap}>{ev.tickets_sold||0}</td>
                          <td className="mono" style={nowrap}>{ev.booking_count||0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {revTotalPages > 1 && (
                  <div className="card-footer d-flex justify-content-between align-items-center py-2 px-3">
                    <small className="text-muted">Page {revPage} of {revTotalPages} &nbsp;({revTotal} events total)</small>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" disabled={revPage<=1} onClick={()=>setRevPage(p=>p-1)}>&lsaquo; Prev</button>
                      <button className="btn btn-sm btn-outline-secondary" disabled={revPage>=revTotalPages} onClick={()=>setRevPage(p=>p+1)}>Next &rsaquo;</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ===== TRANSACTIONS TAB ===== */}
      {tab === "transactions" && (
        <>
          {txnErr && <div className="alert alert-danger">{txnErr}</div>}
          {txnLoading && <div className="text-center py-5"><span className="spinner-border"/></div>}
          {!txnLoading && (
            <div className="bookings-table-card card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Wallet Transactions</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle table-pro" style={txnTblStyle}>
                  <colgroup>
                    <col style={{width:"4%"}}/>
                    <col style={{width:"25%"}}/>
                    <col style={{width:"9%"}}/>
                    <col style={{width:"16%"}}/>
                    <col style={{width:"29%"}}/>
                    <col style={{width:"17%"}}/>
                  </colgroup>
                  <thead>
                    <tr>
                      <th>SI.NO</th>
                      <th>Txn ID</th>
                      <th>Direction</th>
                      <th>Amount</th>
                      <th>Note</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.length===0 && (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">No transactions found.</td></tr>
                    )}
                    {txnSlice.map((t,i) => (
                      <tr key={t.transaction_id||i}>
                        <td style={nowrap}>{(txnPage-1)*TXN_PAGE+i+1}</td>
                        <td className="mono" style={{whiteSpace:"nowrap",fontSize:11}}>{t.transaction_id||"—"}</td>
                        <td style={nowrap}>
                          <span className={`badge bg-${t.direction==="CR"?"success":"danger"}`}>
                            {t.direction||"—"}
                          </span>
                        </td>
                        <td className="mono" style={nowrap}>{fmt(t.amount)}</td>
                        <td style={{fontSize:12,whiteSpace:"normal",overflowWrap:"break-word"}}>{t.note||"—"}</td>
                        <td style={{whiteSpace:"nowrap",fontSize:12}}>{fmtDate(t.created_at_local||t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {txnTotalPages > 1 && (
                <div className="card-footer d-flex justify-content-between align-items-center py-2 px-3">
                  <small className="text-muted">Page {txnPage} of {txnTotalPages} &nbsp;({txns.length} transactions total)</small>
                  <div className="d-flex gap-1">
                    <button className="btn btn-sm btn-outline-secondary" disabled={txnPage<=1} onClick={()=>setTxnPage(p=>p-1)}>&lsaquo; Prev</button>
                    <button className="btn btn-sm btn-outline-secondary" disabled={txnPage>=txnTotalPages} onClick={()=>setTxnPage(p=>p+1)}>Next &rsaquo;</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
